/**
 * group-member-request.js
 */

import React from 'react';
import axios from 'axios';
import { useMediaQuery, Container, Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useParams, useHistory } from 'react-router-dom';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import PageTitle from 'components/page-title';
import FeatureBox from 'components/feature-box';
import Button from 'components/button';


const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
}));


export default function GroupMemberRequest(props) {
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const isSizeXS = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const history = useHistory();
  const params = useParams();
  const [loading, setLoading] = React.useState(true);
  const [info, setInfo] = React.useState();
  const code = params.code;


  React.useEffect(() => {
    document.title = 'PACKTRACK - Group Member Request';
    setAppBar('Group Member Request');
  }, [setAppBar]);

  
  React.useEffect(() => {
    (async() => {
      setLoading(true);
      try {
        const response = await axios.get(`/groups/member-request/${code}`, { bypassErrorHandling: true });
        if (response.status === 200) {
          const data = response.data;
          setInfo({ 
            state: 'ready',
            text: getRequestSummary(data.group, data.request.type, data.request.name) 
          });
        }   
      } catch (e) {
        if (e.response?.status === 404) {
          setInfo({ 
            state: 'error',
            text: `We're sorry, something went wrong. The request code you used no longer exists. It may have already been accepted or was deleted by its creator.` 
          });
        }
      }
      setLoading(false);
    })();
  }, [code]);
  

  const getRequestSummary = (group, type, memberName) => {
    if (type === Types.MemberRequest.TO_USER) {
      return <>You have been requested to join the training group <b>{group.name}</b>.</>;
    } else if (type === Types.MemberRequest.FROM_USER) {
      return <>{memberName} has requested to join the training group <b>{group.name}</b>.</>;
    }
  }


  const onAccept = async () => {
    setLoading(true);
    const response = await axios.post(`/groups/member-request/${code}`);
    if (response.status === 200) {
      setInfo({
        state: 'accepted',
        text: 'You have successfully accepted this member request!'
      });
    }
    setLoading(false);
  }


  return (
    <Container className={classes.root}>

      {/* title */}
      {!isSizeXS &&
      <Box mb={4}>
        <PageTitle iconName='group' title='Group Member Request' />
      </Box>}

      <FeatureBox style={{ minHeight: 140 }} loading={loading}>
        {info &&
        <Typography className={info?.state === 'error' ? 'text-error' : ''}>{info.text}</Typography>}
        
        {info?.state === 'ready' &&
        <Button
          variant='contained'
          style={{ marginTop: 24 }}
          onClick={onAccept}
          data-testid='accept-request-button'
          children='Accept'
        />}

        {['accepted', 'error'].includes(info?.state) &&
        <Button
          variant='contained'
          color='primary'
          style={{ marginTop: 24 }}
          onClick={() => history.push(user.role.isSupervisor ? '/manage' : '/groups')}
          children={`Go To ${user.role.isSupervisor ? 'Manage' : 'Groups'}`}
        />}
      </FeatureBox>
      
    </Container>
  );
}